/**
 * `@shn/phg-client-demo-data` — synthetic seed data for Surface 1
 * hosted patient walkthrough.
 *
 * Bounded workspace per CTO disposition cycle on PR #210 (Workstream
 * 5; June 9 2026 RHTP Summit ship). Production `@shn/phg-client-web`
 * does NOT bundle demo data; production deployments don't include
 * this workspace; demo deployments (the Surface 1 hosted scaffold)
 * compose both via the wrapper-injection seam (PR #210 concern #3
 * disposition).
 *
 * **No real PHI at any point.** All synthetic; hand-curated for v1;
 * v1.5+ migration target = W6 `shn-synthetic` toolkit-generated FHIR
 * R4 USCDI v3 + X12 837 + NCPDP per PR #210 §5.2 (DEV2 portfolio;
 * Sprint 3).
 *
 * **3-scene minimum-viable hand-curation.** This package carries the
 * §9 Risk 2 contraction-plan content (Activation + Aggregation +
 * Patient Projection scenes); Recovery + Third-party-app-consent
 * scenes follow on if Sprint 1+2 capacity permits per the contraction
 * lever. 5 holders + 12 audit-chain records spanning the v1 four-frame
 * authority model — see `synthetic-holders.ts` + `synthetic-audit-records.ts`.
 */
import { SYNTHETIC_AUDIT_CHAIN_RECORDS } from './synthetic-audit-records.js';
import { SYNTHETIC_HOLDERS } from './synthetic-holders.js';
/**
 * Bounded v1 four-frame subset for Surface 1 sectoral-category
 * labeling per CTO disposition on PR #210 concern #4: stable v1
 * authority-frame model on Accepted ADR-022 + ADR-009; PR #195
 * PCISchema disposition cycle is orthogonal.
 *
 * Maps CTO's "TPO + patient-rights + public-health + research"
 * framing to canonical `AuthorityFrameCategory` values from
 * `@shn/substrate-types`. Other categories (delegation,
 * emergency-access, use-authority-certification, agent-profile-
 * certification, sectoral-statute, patient-consent, other) are
 * deliberately OUT of v1 demo scope — narrative simplification per
 * §5.4 coherence test.
 */
export const V1_AUTHORITY_FRAMES = [
    'tpo',
    'patient-rights',
    'public-health-statute',
    'irb-approval',
];
/**
 * Returns the v1 hand-curated synthetic seed for Surface 1 — 5
 * holders + 12 audit-chain records spanning the v1 four-frame
 * authority model. Stable reference; the underlying arrays are
 * frozen per `Object.freeze`, so callers can safely cache + share
 * without defensive copying.
 *
 * The wrapper-injection seam (PR #210 concern #3 disposition; future
 * PR) consumes this seed at Surface 1 composition root: synthetic
 * holders feed `MockProjectionServiceClient.fetchHolderInventory`;
 * synthetic audit-chain records feed `writePatientProjectionRecord`
 * for pre-encrypt + write-to-mock-store under the production active-
 * session KEK (matching the `<PatientProjectionView />` post-sign-in
 * code path per §5.4 coherence test).
 */
export function getSurfaceOneSeed() {
    return {
        version: 'v1',
        holders: SYNTHETIC_HOLDERS,
        auditChainRecords: SYNTHETIC_AUDIT_CHAIN_RECORDS,
    };
}
//# sourceMappingURL=index.js.map